import React, { useState, useRef, useMemo } from "react";
import { collection, addDoc } from "firebase/firestore";
import { db, isFirebaseConfigured } from "../firebase";
import { useFirestore } from "../contexts/FirestoreContext";
import { parseThreadsUsername } from "../utils/threads";
import confetti from "canvas-confetti";

// Keep in sync with the `prayers` create rule in firestore.rules.
const PRAYER_MAX = 3000;
const THREADS_INPUT_MAX = 200; // accepts a full Threads profile URL

const Form: React.FC = () => {
  const [threadsInput, setThreadsInput] = useState("");
  const [matchId, setMatchId] = useState("");
  const [prayer, setPrayer] = useState("");
  const [showIncense, setShowIncense] = useState(false);
  const [animateOut, setAnimateOut] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [thankPopup, setThankPopup] = useState<string | null>(null);
  const [burst, setBurst] = useState(false);
  const [prayedFor, setPrayedFor] = useState("");
  const [submitError, setSubmitError] = useState("");
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Rising embers shown during the incense ritual (deterministic, cheap).
  const embers = useMemo(
    () =>
      Array.from({ length: 16 }, (_, i) => ({
        left: (i * 6.1 + (i % 4) * 5) % 100,
        size: 4 + (i % 4) * 3,
        duration: 4 + (i % 5),
        delay: (i % 6) * 0.35,
        drift: ((i % 5) - 2) * 26,
      })),
    []
  );

  const { matches: allMatches, thankMessages, refreshPrayers } = useFirestore();

  // Offer exactly what the calendar shows: any match that hasn't ended yet
  // (mirrors MatchCalendar's "upcoming" logic), so anything visible in
  // "Lịch thi đấu" is selectable for prayer regardless of the Mở khấn toggle.
  const MATCH_DURATION_MS = 4 * 60 * 60 * 1000;
  const matches = allMatches.filter((m) => {
    const now = new Date();
    const matchStart = new Date(`${m.date}T${m.time}:00`);
    const matchEnd = new Date(matchStart.getTime() + MATCH_DURATION_MS);
    return matchEnd >= now || (m.isActive && new Date(m.date + "T23:59:59") >= now);
  });

  const prayerTracks = [
    "https://t1-pray.pages.dev/UITPray.mp3",
    "https://t1-pray.pages.dev/UITPray2.mp3",
  ];

  const getRandomThankMessage = (): string => {
    if (thankMessages.length === 0) {
      return "Cảm ơn bạn đã cầu nguyện cho T1!";
    }
    const idx = Math.floor(Math.random() * thankMessages.length);
    return thankMessages[idx].message;
  };

  const playEffects = () => {
    setShowIncense(true);
    setAnimateOut(false);
    // Created on demand (not on mount) so page loads don't download audio;
    // a missing or blocked track must never break the ritual.
    audioRef.current = new Audio(prayerTracks[Math.floor(Math.random() * prayerTracks.length)]);
    audioRef.current.play().catch(() => {});

    confetti({
      particleCount: 150,
      spread: 80,
      origin: { y: 0.6 },
      colors: ["#DC2626", "#FFFFFF", "#EF4444", "#FCA5A5"],
    });

    setTimeout(() => {
      confetti({
        particleCount: 80,
        spread: 100,
        origin: { x: 0.3, y: 0.5 },
        colors: ["#DC2626", "#FFFFFF"],
      });
      confetti({
        particleCount: 80,
        spread: 100,
        origin: { x: 0.7, y: 0.5 },
        colors: ["#DC2626", "#FFFFFF"],
      });
    }, 500);

    setTimeout(() => {
      setAnimateOut(true);
      setTimeout(() => {
        setShowIncense(false);
      }, 1000);
    }, 4000);
  };

  const handleSubmit = async () => {
    if (!threadsInput.trim() || !prayer.trim()) return;

    setIsSubmitting(true);
    setSubmitError("");
    setBurst(true);
    setTimeout(() => setBurst(false), 600);

    const selectedMatch = matches.find((m) => m.id === matchId);
    const label = selectedMatch ? `T1 vs ${selectedMatch.opponent}` : "";
    setPrayedFor(label);

    try {
      const username = parseThreadsUsername(threadsInput);

      if (isFirebaseConfigured) {
        await addDoc(collection(db, "prayers"), {
          threadsUsername: username,
          prayerContent: prayer.trim(),
          matchId: matchId || "",
          matchLabel: label,
          createdAt: Date.now(),
        });
        // Refresh prayers list after submission
        refreshPrayers();
      }

      playEffects();
      setThankPopup(getRandomThankMessage());

      setThreadsInput("");
      setMatchId("");
      setPrayer("");
    } catch (error) {
      // Keep the text so the fan can retry instead of losing it silently.
      console.error("Error saving prayer:", error);
      setSubmitError("Chưa gửi được lời khấn, bạn thử lại nhé!");
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass =
    "w-full p-3.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/25 focus:border-primary/60 focus:bg-white/[0.07] focus:outline-none focus:ring-1 focus:ring-primary/30 transition-all font-body text-sm";

  return (
    <div className="w-full">
      <div className="animated-border glass-card rounded-2xl p-6 md:p-8 space-y-5">
        {/* Threads input */}
        <div>
          <label className="block text-white/60 text-xs uppercase tracking-wider mb-2 font-heading font-medium">
            Tên Threads
          </label>
          <div className="relative">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30 text-sm font-bold font-heading">
              @
            </span>
            <input
              type="text"
              placeholder="@username hoặc link Threads"
              value={threadsInput}
              onChange={(e) => setThreadsInput(e.target.value)}
              maxLength={THREADS_INPUT_MAX}
              className={inputClass + " pl-10"}
            />
          </div>
        </div>

        {/* Match selector + Prayer side by side */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="md:w-5/12">
            <label className="block text-white/60 text-xs uppercase tracking-wider mb-2 font-heading font-medium">
              Trận muốn khấn
            </label>
            <select
              value={matchId}
              onChange={(e) => setMatchId(e.target.value)}
              className={inputClass + " appearance-none cursor-pointer"}
            >
              <option value="">Khấn chung cho T1</option>
              {matches.map((match) => (
                <option key={match.id} value={match.id}>
                  vs {match.opponent} -{" "}
                  {new Date(match.date + "T00:00:00").toLocaleDateString(
                    "vi-VN",
                    { day: "2-digit", month: "2-digit" }
                  )}
                </option>
              ))}
            </select>
          </div>

          <div className="md:w-7/12">
            <label className="block text-white/60 text-xs uppercase tracking-wider mb-2 font-heading font-medium">
              Lời khấn
            </label>
            <textarea
              placeholder="Nhập lời khấn..."
              value={prayer}
              onChange={(e) => setPrayer(e.target.value)}
              maxLength={PRAYER_MAX}
              rows={3}
              className={inputClass + " resize-none"}
            />
          </div>
        </div>

        {/* Submit button */}
        <button
          onClick={handleSubmit}
          disabled={isSubmitting || !threadsInput.trim() || !prayer.trim()}
          className={`w-full p-4 bg-gradient-to-r from-primary to-primary-dark hover:from-primary-light hover:to-primary text-white font-heading font-bold text-base uppercase tracking-wider rounded-xl transition-all duration-300 active:scale-[0.98] disabled:opacity-30 disabled:cursor-not-allowed shadow-[0_0_30px_rgba(220,38,38,0.3)] hover:shadow-[0_0_40px_rgba(220,38,38,0.5)] disabled:shadow-none cursor-pointer ${
            burst ? "btn-burst" : ""
          }`}
        >
          {isSubmitting ? "Đang khấn..." : "THẮP HƯƠNG"}
        </button>
        {submitError && (
          <p role="alert" className="text-center text-sm text-red-400 font-body">
            {submitError}
          </p>
        )}
      </div>

      {showIncense && (
        <>
          <div className={`overlay ${showIncense ? "active" : ""}`}>
            {embers.map((e, idx) => (
              <span
                key={idx}
                className="ember-particle"
                style={
                  {
                    left: `${e.left}%`,
                    width: `${e.size}px`,
                    height: `${e.size}px`,
                    animationDuration: `${e.duration}s`,
                    animationDelay: `${e.delay}s`,
                    "--drift": `${e.drift}px`,
                  } as React.CSSProperties
                }
              />
            ))}
          </div>
          <img
            src="https://i.imgur.com/RIPG0ym.gif"
            className={`incense-stick ${
              animateOut ? "incense-hidden" : "incense-visible"
            }`}
            alt="Incense Stick"
            style={{ zIndex: 20 }}
          />
        </>
      )}

      {/* Thank you popup */}
      {thankPopup && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          onClick={() => setThankPopup(null)}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div
            className="relative glass-card rounded-2xl p-8 md:p-10 max-w-md w-full text-center animate-thank-popup"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-0.5 bg-primary mx-auto mb-6" />
            <div className="text-4xl mb-4">🙏</div>
            {prayedFor && (
              <p className="text-primary text-sm font-heading font-semibold uppercase tracking-wider mb-3">
                Đã khấn cho {prayedFor}
              </p>
            )}
            <p className="text-white text-lg md:text-xl font-heading font-bold leading-relaxed mb-2">
              {thankPopup}
            </p>
            <div className="w-12 h-0.5 bg-primary mx-auto mt-6 mb-6" />
            <button
              onClick={() => setThankPopup(null)}
              className="px-8 py-3 bg-primary/10 border border-primary/20 text-primary hover:bg-primary/20 rounded-xl transition text-sm font-body cursor-pointer"
            >
              Đóng
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Form;
